import React, { useEffect } from "react";
import {
    Row,
    Col,
    Form,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Badge,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { useMikroSettings } from "../hooks/useMikroSettings";

const NAVY = "#0A316E";

const STATUS_META = {
    PENDING: { label: "Bekliyor", color: "#B0B7C3" },
    SENT: { label: "Gönderildi", color: "#71C30B" },
    FAILED: { label: "Başarısız", color: "#dc3545" },
    CANCELLED: { label: "İptal", color: "#E07A7A" },
    SKIPPED: { label: "Atlandı", color: "#B0B7C3" },
};

const OPERATION_LABELS = {
    ADD_INVOICE: "Fatura",
    ADD_COLLECTION: "Tahsilat",
    CANCEL_INVOICE: "Fatura İptali",
    CANCEL_COLLECTION: "Tahsilat İptali",
};

const styles = `
  .mikro-modern .table thead tr { background-color: #f9f9f9; }
  .mikro-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
  .mikro-modern .table td { vertical-align: middle; }
  .mikro-modern .table-hover tbody tr:hover { background: #fcfcfd; }
  .mikro-modern .btn-rounded { border-radius: 999px; }
  .mikro-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
  .mikro-modern .btn-navy:hover { filter: brightness(1.1); }
  .mikro-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
`;

// Sunucuda tanımlı sır alanları için "yeniden girin" ipucu
const SecretHint = ({ isSet }) =>
    isSet ? (
        <small className="text-muted fw-normal">(tanımlı — değiştirmek için yeniden girin)</small>
    ) : null;

export default function MikroSettingsTab({ companyId, otherActiveLabel, onSaved }) {
    const {
        formik,
        loading,
        hasSettings,
        connected,
        secretsSet,
        testing,
        testConnection,
        queue,
        queueLoading,
        queueStatus,
        changeQueueStatus,
        loadQueue,
        retryingId,
        retryItem,
    } = useMikroSettings(companyId, onSaved);

    useEffect(() => {
        if (hasSettings) loadQueue(0);
    }, [hasSettings]);

    const err = (name) =>
        formik.touched[name] && formik.errors[name] ? (
            <div className="invalid-feedback d-block">{formik.errors[name]}</div>
        ) : null;

    const isInvalid = (name) => formik.touched[name] && !!formik.errors[name];

    return (
        <div className="mikro-modern">
            <style>{styles}</style>

            <p className="text-muted mb-4">
                Cari hareketleriniz (borç/fatura ve tahsilat) Mikro Jump Bulut ERP'ye otomatik aktarılır.
                API Key, KeyToken ve kullanıcı bilgilerinizi Mikro yetkilinizden veya Jump Bulut yönetim
                panelinden temin edebilirsiniz.
            </p>

            {hasSettings && !connected && (
                <Alert color="warning" className="border-0 shadow-sm">
                    <i className="bx bx-error-circle me-2"></i>
                    Kimlik bilgileri eksik. Aktarımın çalışması için API Key, Firma Kodu, Kullanıcı Kodu,
                    Parola ve Çalışma Yılı alanlarının dolu olması gerekir.
                </Alert>
            )}

            <Form onSubmit={formik.handleSubmit}>
                <h6 className="fw-bold mb-3" style={{ color: NAVY }}>
                    <i className="bx bx-key me-2"></i>Kimlik Bilgileri
                </h6>

                <Row className="g-3">
                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-apiKey">
                            API Key <SecretHint isSet={secretsSet.apiKey} />
                        </Label>
                        <Input
                            id="mikro-apiKey"
                            name="apiKey"
                            type="password"
                            className="pill-input"
                            placeholder={secretsSet.apiKey ? "••••••••" : "Mikro tarafından verilen API Key"}
                            value={formik.values.apiKey}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("apiKey")}
                            autoComplete="new-password"
                        />
                        {err("apiKey")}
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-keyToken">
                            KeyToken <SecretHint isSet={secretsSet.keyToken} />
                        </Label>
                        <Input
                            id="mikro-keyToken"
                            name="keyToken"
                            type="password"
                            className="pill-input"
                            placeholder={secretsSet.keyToken ? "••••••••" : "Jump Bulut gateway token'ı"}
                            value={formik.values.keyToken}
                            onChange={formik.handleChange}
                            autoComplete="new-password"
                        />
                        <small className="text-muted">Jump Bulut gateway kullanıyorsanız zorunludur.</small>
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-kullaniciKodu">Kullanıcı Kodu</Label>
                        <Input
                            id="mikro-kullaniciKodu"
                            name="kullaniciKodu"
                            className="pill-input"
                            placeholder="Mikro kullanıcı kodu"
                            value={formik.values.kullaniciKodu}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("kullaniciKodu")}
                        />
                        {err("kullaniciKodu")}
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-sifre">
                            Parola <SecretHint isSet={secretsSet.sifre} />
                        </Label>
                        <Input
                            id="mikro-sifre"
                            name="sifre"
                            type="password"
                            className="pill-input"
                            placeholder={secretsSet.sifre ? "••••••••" : "Mikro kullanıcı parolası"}
                            value={formik.values.sifre}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("sifre")}
                            autoComplete="new-password"
                        />
                        {err("sifre")}
                    </Col>
                </Row>

                <h6 className="fw-bold mt-4 mb-3" style={{ color: NAVY }}>
                    <i className="bx bx-buildings me-2"></i>Firma Bağlamı
                </h6>

                <Row className="g-3">
                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-firmaKodu">Firma Kodu</Label>
                        <Input
                            id="mikro-firmaKodu"
                            name="firmaKodu"
                            className="pill-input"
                            placeholder="Örn. MIKRO"
                            value={formik.values.firmaKodu}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("firmaKodu")}
                        />
                        {err("firmaKodu")}
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-calismaYili">Çalışma Yılı</Label>
                        <Input
                            id="mikro-calismaYili"
                            name="calismaYili"
                            type="number"
                            className="pill-input"
                            value={formik.values.calismaYili}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("calismaYili")}
                        />
                        {err("calismaYili")}
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-firmaNo">Firma No</Label>
                        <Input
                            id="mikro-firmaNo"
                            name="firmaNo"
                            type="number"
                            className="pill-input"
                            value={formik.values.firmaNo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("firmaNo")}
                        />
                        {err("firmaNo")}
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-subeNo">Şube No</Label>
                        <Input
                            id="mikro-subeNo"
                            name="subeNo"
                            type="number"
                            className="pill-input"
                            value={formik.values.subeNo}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            invalid={isInvalid("subeNo")}
                        />
                        {err("subeNo")}
                    </Col>
                </Row>

                <h6 className="fw-bold mt-4 mb-3" style={{ color: NAVY }}>
                    <i className="bx bx-wallet me-2"></i>Tahsilat Hedefleri
                </h6>

                <Row className="g-3">
                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-defaultKasaKodu">Varsayılan Kasa Kodu</Label>
                        <Input
                            id="mikro-defaultKasaKodu"
                            name="defaultKasaKodu"
                            className="pill-input"
                            placeholder="Nakit tahsilatların işleneceği kasa"
                            value={formik.values.defaultKasaKodu}
                            onChange={formik.handleChange}
                        />
                    </Col>

                    <Col md={6}>
                        <Label className="fw-semibold" htmlFor="mikro-defaultBankaKodu">Varsayılan Banka Kodu</Label>
                        <Input
                            id="mikro-defaultBankaKodu"
                            name="defaultBankaKodu"
                            className="pill-input"
                            placeholder="Kart/havale tahsilatlarının işleneceği banka"
                            value={formik.values.defaultBankaKodu}
                            onChange={formik.handleChange}
                        />
                    </Col>

                    <Col md={12}>
                        <Label className="fw-semibold" htmlFor="mikro-apiBaseUrl">
                            API Adresi <small className="text-muted fw-normal">(opsiyonel)</small>
                        </Label>
                        <Input
                            id="mikro-apiBaseUrl"
                            name="apiBaseUrl"
                            className="pill-input"
                            placeholder="Boş bırakılırsa Jump Bulut varsayılan gateway'i kullanılır"
                            value={formik.values.apiBaseUrl}
                            onChange={formik.handleChange}
                        />
                    </Col>

                    <Col md={12} className="mt-3">
                        <div className="form-check form-switch">
                            <Input
                                id="mikro-enabled"
                                name="enabled"
                                type="switch"
                                role="switch"
                                checked={formik.values.enabled}
                                onChange={formik.handleChange}
                            />
                            <Label className="form-check-label" htmlFor="mikro-enabled">
                                Entegrasyon aktif
                            </Label>
                        </div>
                        {formik.values.enabled && otherActiveLabel && (
                            <div className="small mt-2" style={{ color: "#9a6700" }}>
                                <i className="bx bx-info-circle me-1"></i>
                                Kaydettiğinizde aktif olan <strong>{otherActiveLabel}</strong> entegrasyonu
                                otomatik olarak pasifleştirilecek.
                            </div>
                        )}
                    </Col>
                </Row>

                <div className="d-flex flex-wrap gap-2 mt-4">
                    <Button type="submit" className="btn-navy btn-rounded px-4" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : <i className="bx bx-save me-1"></i>}{" "}
                        {hasSettings ? "Güncelle" : "Kaydet"}
                    </Button>
                    <Button
                        type="button"
                        color="light"
                        className="btn-rounded px-4"
                        onClick={testConnection}
                        disabled={testing || !hasSettings || !connected}
                        title={
                            !connected
                                ? "Bağlantı testi için kimlik bilgileri kaydedilmiş olmalı"
                                : undefined
                        }
                    >
                        {testing ? <Spinner size="sm" /> : <i className="bx bx-plug me-1"></i>}{" "}
                        Bağlantıyı Test Et
                    </Button>
                </div>
            </Form>

            {hasSettings && (
                <>
                    <hr className="my-4" />

                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <h5 className="mb-0 fw-bold" style={{ color: NAVY }}>
                            <i className="bx bx-transfer-alt me-2"></i>Senkron Kuyruğu
                        </h5>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                type="select"
                                bsSize="sm"
                                className="pill-input"
                                style={{ width: 160 }}
                                value={queueStatus}
                                onChange={(e) => changeQueueStatus(e.target.value)}
                            >
                                <option value="">Tüm Durumlar</option>
                                <option value="PENDING">Bekliyor</option>
                                <option value="SENT">Gönderildi</option>
                                <option value="FAILED">Başarısız</option>
                                <option value="CANCELLED">İptal</option>
                                <option value="SKIPPED">Atlandı</option>
                            </Input>
                            <Button
                                color="light"
                                size="sm"
                                className="btn-rounded"
                                onClick={() => loadQueue(queue.page)}
                                disabled={queueLoading}
                            >
                                <i className="bx bx-refresh"></i>
                            </Button>
                        </div>
                    </div>

                    {queueLoading ? (
                        <div className="text-center p-4">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Kuyruk yükleniyor...</div>
                        </div>
                    ) : queue.content.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            <i className="bx bx-transfer-alt fs-2 d-block mb-2"></i>
                            Senkron kaydı bulunamadı. Yeni cari hareketler oluştukça burada listelenir.
                        </div>
                    ) : (
                        <>
                            <Table className="table table-hover">
                                <Thead>
                                    <Tr>
                                        <Th>İşlem</Th>
                                        <Th>Belge No</Th>
                                        <Th>Evrak No</Th>
                                        <Th>Durum</Th>
                                        <Th>Deneme</Th>
                                        <Th>Hata</Th>
                                        <Th>Tarih</Th>
                                        <Th className="text-end">Aksiyon</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {queue.content.map((item) => {
                                        const meta = STATUS_META[item.status] || STATUS_META.PENDING;
                                        return (
                                            <Tr key={item.id}>
                                                <Td>{OPERATION_LABELS[item.operation] || item.operation}</Td>
                                                <Td className="font-monospace small">
                                                    {item.documentNumber || "-"}
                                                </Td>
                                                <Td className="font-monospace small">
                                                    {item.mikroEvrakNo || "-"}
                                                </Td>
                                                <Td>
                                                    <Badge
                                                        pill
                                                        style={{
                                                            backgroundColor: meta.color,
                                                            borderRadius: 999,
                                                        }}
                                                    >
                                                        {meta.label}
                                                    </Badge>
                                                </Td>
                                                <Td>{item.attempts}</Td>
                                                <Td
                                                    className="small text-danger"
                                                    style={{ maxWidth: 240 }}
                                                    title={item.lastError || ""}
                                                >
                                                    {item.lastError
                                                        ? item.lastError.length > 60
                                                            ? item.lastError.slice(0, 60) + "…"
                                                            : item.lastError
                                                        : "-"}
                                                </Td>
                                                <Td className="small text-muted">
                                                    {item.createdAt
                                                        ? new Date(item.createdAt).toLocaleDateString("tr-TR")
                                                        : "-"}
                                                </Td>
                                                <Td className="text-end">
                                                    {item.status === "FAILED" && (
                                                        <Button
                                                            size="sm"
                                                            className="btn-navy btn-rounded"
                                                            onClick={() => retryItem(item.id)}
                                                            disabled={retryingId === item.id}
                                                        >
                                                            {retryingId === item.id ? (
                                                                <Spinner size="sm" />
                                                            ) : (
                                                                <>
                                                                    <i className="bx bx-revision me-1"></i>
                                                                    Yeniden Dene
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>

                            {queue.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        Toplam {queue.totalElements} kayıt
                                    </small>
                                    <div className="d-flex gap-1">
                                        <Button
                                            size="sm"
                                            color="light"
                                            className="btn-rounded"
                                            disabled={queue.page === 0}
                                            onClick={() => loadQueue(queue.page - 1)}
                                        >
                                            <i className="bx bx-chevron-left"></i>
                                        </Button>
                                        <span className="align-self-center small text-muted px-2">
                                            {queue.page + 1} / {queue.totalPages}
                                        </span>
                                        <Button
                                            size="sm"
                                            color="light"
                                            className="btn-rounded"
                                            disabled={queue.page + 1 >= queue.totalPages}
                                            onClick={() => loadQueue(queue.page + 1)}
                                        >
                                            <i className="bx bx-chevron-right"></i>
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
}
